/**
 * Project Report — preview + PDF download.
 *
 * Two render shells share the same body:
 *   - `ProjectReportPanel`   — docked rail (renders inside a card on
 *                              the Timeline tab; no overlay chrome)
 *   - `ProjectReportSlideOver` — full-width slide-over (legacy entry
 *                                that wraps the same panel with the
 *                                .slide-over header)
 *
 * Mirrors mobile's `TaskReportModal`
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/projects/TaskReportModal.tsx
 *
 * Sections:
 *   1. Project header — name, address, generated date
 *   2. Period — earliest task start → latest task end
 *   3. Summary stat cards — total, completed, ongoing, not started
 *   4. Overall weighted-progress bar
 *   5. Category breakdown table
 *   6. Detailed task log (chronological)
 *
 * Action bar (sticky bottom):
 *   - Progress Report PDF (primary) — opens HTML in new tab + browser print dialog
 *   - Timeline Agreement PDF (secondary)
 *   - Copy share text (plain text version)
 */
import { useEffect, useMemo, useState } from 'react';

import {
  buildProgressReportHtml,
  buildProjectReport,
  buildTimelineAgreementHtml,
  openHtmlForPrint,
} from '@/lib/projectReports';
import type { TaskItem } from '@/lib/useProjectTasks';
import type { ProjectListItem } from '@/lib/useProjects';

const STATUS_TINT = {
  completed: { bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  ongoing: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)' },
  not_started: { bg: '#F3F4F6', fg: 'var(--text-secondary)' },
} as const;

const STATUS_LABEL = {
  completed: 'DONE',
  ongoing: 'ONGOING',
  not_started: 'PENDING',
} as const;

/**
 * Embeddable panel. Renders the full report content WITHOUT
 * overlay chrome — designed to sit inside a card on the Timeline
 * tab (right rail) alongside the Calendar / List view.
 */
export function ProjectReportPanel({
  project,
  tasks,
  onClose,
  variant = 'rail',
}: {
  project: ProjectListItem | null;
  tasks: TaskItem[];
  /** Optional close handler — rendered as a chevron in the rail
   *  header when set. The slide-over wrapper passes its own. */
  onClose?: () => void;
  /** Visual mode. `'rail'` = compact for sticky right column;
   *  `'slide'` = full body inside slide-over. */
  variant?: 'rail' | 'slide';
}) {
  const report = useMemo(
    () => buildProjectReport(project, tasks),
    [project, tasks],
  );
  const [copied, setCopied] = useState(false);

  const onProgressPdf = () => {
    openHtmlForPrint(
      buildProgressReportHtml(report),
      `${report.projectName} — Progress Report`,
    );
  };

  const onTimelinePdf = () => {
    openHtmlForPrint(
      buildTimelineAgreementHtml(report),
      `${report.projectName} — Timeline Agreement`,
    );
  };

  const onCopyShareText = async () => {
    const lines: string[] = [];
    lines.push(`PROJECT TIMESHEET`);
    lines.push(report.projectName);
    if (report.projectAddress) lines.push(report.projectAddress);
    lines.push(`Generated ${report.generatedOn}`);
    lines.push('');
    lines.push(
      `Period: ${report.periodStart ?? '—'} → ${report.periodEnd ?? '—'}  (${report.totalDays} days)`,
    );
    lines.push(
      `Tasks: ${report.total}  ·  Done: ${report.completed}  ·  Ongoing: ${report.ongoing}  ·  Pending: ${report.notStarted}`,
    );
    lines.push(`Overall progress: ${report.weightedProgress}%`);
    if (report.categories.length > 0) {
      lines.push('');
      lines.push('BY CATEGORY');
      for (const c of report.categories) {
        lines.push(
          `  • ${c.label} — ${c.count} task${c.count === 1 ? '' : 's'}, ${c.totalDays} days, avg ${c.avgProgress}%`,
        );
      }
    }
    lines.push('');
    lines.push('DETAILED LOG');
    report.taskRows.forEach((t, i) => {
      lines.push(
        `${String(i + 1).padStart(2, '0')}. ${t.title}  [${t.status.toUpperCase()}]`,
      );
      lines.push(`    ${t.category}${t.assignee ? `  ·  ${t.assignee}` : ''}`);
      lines.push(`    ${t.start} → ${t.end}  (${t.durationLabel})  · ${t.progress}%`);
      if (t.description) lines.push(`    ${t.description}`);
    });
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      // Fallback for non-secure contexts — open a window with the text.
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(
          `<pre style="white-space:pre-wrap;font-family:monospace;padding:24px;">${text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')}</pre>`,
        );
      }
    }
  };

  const isRail = variant === 'rail';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#fff',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: isRail ? '12px 16px' : '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: isRail ? 13 : 16,
            fontWeight: 700,
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className="ph ph-file-text" /> Project Report
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: 18,
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
            }}
          >
            <i className="ph ph-x" />
          </button>
        ) : null}
      </div>

      {/* Scrollable body — `minHeight: 0` is essential: without it
          flexbox refuses to shrink this child below its content
          height, so `overflowY: auto` never triggers and the bottom
          action bar gets pushed off-screen. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: isRail ? 16 : 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
          {/* Header */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '1.4px',
                color: 'var(--brand)',
                fontFamily: 'monospace',
              }}
            >
              INTERIOR OS · PROGRESS REPORT
            </div>
            <h3
              style={{
                fontSize: 22,
                fontWeight: 800,
                margin: '8px 0 4px',
                color: 'var(--text-primary)',
                letterSpacing: '-0.5px',
              }}
            >
              {report.projectName}
            </h3>
            {report.projectAddress ? (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {report.projectAddress}
              </div>
            ) : null}
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              Generated {report.generatedOn}
            </div>
          </div>

          {/* Period */}
          <div>
            <SectionHeader>Project period</SectionHeader>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: 12,
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <PeriodCol kicker="START" value={report.periodStart ?? '—'} />
              <span style={{ color: 'var(--text-tertiary)' }}>→</span>
              <PeriodCol kicker="END" value={report.periodEnd ?? '—'} />
              <PeriodCol kicker="TOTAL SPAN" value={`${report.totalDays} days`} />
              {report.workDays > 0 ? (
                <PeriodCol kicker="WORK-DAYS" value={String(report.workDays)} />
              ) : null}
            </div>
          </div>

          {/* Summary stats */}
          <div>
            <SectionHeader>Summary</SectionHeader>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <StatBox label="Total tasks" value={report.total} />
              <StatBox
                label="Completed"
                value={report.completed}
                bg="var(--success-bg)"
              />
              <StatBox
                label="Ongoing"
                value={report.ongoing}
                bg="var(--warning-bg)"
              />
              <StatBox
                label="Not started"
                value={report.notStarted}
                bg="#F1F5F9"
              />
            </div>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '1.4px',
                    color: 'var(--text-tertiary)',
                    fontFamily: 'monospace',
                  }}
                >
                  OVERALL PROGRESS (DURATION-WEIGHTED)
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'var(--brand)',
                  }}
                >
                  {report.weightedProgress}%
                </div>
              </div>
              <div
                style={{
                  height: 7,
                  background: '#F1F5F9',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${report.weightedProgress}%`,
                    background: 'var(--brand)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Category breakdown */}
          {report.categories.length > 0 ? (
            <div>
              <SectionHeader>By category</SectionHeader>
              <table className="proj-table" style={{ marginTop: 0 }}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="right">Tasks</th>
                    <th className="right">Days</th>
                    <th className="right">Avg %</th>
                  </tr>
                </thead>
                <tbody>
                  {report.categories.map((c) => (
                    <tr key={c.key}>
                      <td>{c.label}</td>
                      <td className="right tn">{c.count}</td>
                      <td className="right tn">{c.totalDays}</td>
                      <td className="right tn">{c.avgProgress}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* Detailed log */}
          <div>
            <SectionHeader>
              Detailed log · {report.taskRows.length}{' '}
              {report.taskRows.length === 1 ? 'task' : 'tasks'}
            </SectionHeader>
            {report.taskRows.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--text-tertiary)',
                  fontSize: 13,
                  background: '#FAFAFA',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                }}
              >
                No tasks on this timeline yet. Add tasks from the Timeline
                tab to populate this report.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {report.taskRows.map((t, i) => {
                  const tint = STATUS_TINT[t.status];
                  return (
                    <div
                      key={t.id}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: 12,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: 'var(--text-tertiary)',
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          #{String(i + 1).padStart(2, '0')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: 'var(--text-primary)',
                              lineHeight: 1.3,
                            }}
                          >
                            {t.title}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              color: 'var(--brand)',
                              letterSpacing: '0.8px',
                              marginTop: 2,
                            }}
                          >
                            {t.category.toUpperCase()}
                            {t.assignee ? `  ·  ${t.assignee}` : ''}
                          </div>
                        </div>
                        <span
                          style={{
                            background: tint.bg,
                            color: tint.fg,
                            padding: '2px 6px',
                            borderRadius: 9,
                            fontSize: 9,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            letterSpacing: '0.8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {STATUS_LABEL[t.status]}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 16,
                          flexWrap: 'wrap',
                          marginBottom: 8,
                        }}
                      >
                        <DateCol kicker="START" value={t.start} />
                        <DateCol kicker="END" value={t.end} />
                        <DateCol kicker="DURATION" value={t.durationLabel} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            flex: 1,
                            height: 5,
                            background: '#F1F5F9',
                            borderRadius: 3,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              width: `${t.progress}%`,
                              background: 'var(--brand)',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: 'var(--text-secondary)',
                          }}
                        >
                          {t.progress}%
                        </div>
                      </div>
                      {t.description ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-secondary)',
                            marginTop: 8,
                            lineHeight: 1.5,
                          }}
                        >
                          {t.description}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      {/* Footer action bar (lives at bottom of the panel/slide-over) */}
      <div
        style={{
          background: '#fff',
          borderTop: '1px solid var(--border)',
          padding: isRail ? 12 : 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className="btn-primary"
          onClick={onProgressPdf}
          style={{ width: '100%', justifyContent: 'center', gap: 8 }}
        >
          <i className="ph-bold ph-chart-line" /> Progress Report PDF
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onTimelinePdf}
          style={{ width: '100%', justifyContent: 'center', gap: 8 }}
        >
          <i className="ph ph-handshake" /> Timeline Agreement PDF
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={onCopyShareText}
          style={{ width: '100%', justifyContent: 'center', gap: 8 }}
        >
          <i className={`ph ${copied ? 'ph-check' : 'ph-copy'}`} />
          {copied ? 'Copied to clipboard' : 'Copy share-as-text'}
        </button>
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-tertiary)',
            textAlign: 'center',
          }}
        >
          PDFs open in a new tab + browser print dialog.
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Legacy slide-over wrapper — kept for any caller still using the
// overlay mode. Embeds the panel inside a .slide-over chrome.
// ────────────────────────────────────────────────────────────

export function ProjectReportSlideOver({
  open,
  onClose,
  project,
  tasks,
}: {
  open: boolean;
  onClose: () => void;
  project: ProjectListItem | null;
  tasks: TaskItem[];
}) {
  // Body scroll lock + Esc to close (matches other slide-overs).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        style={{ width: 'min(720px, 100vw)', padding: 0 }}
      >
        <ProjectReportPanel
          project={project}
          tasks={tasks}
          onClose={onClose}
          variant="slide"
        />
      </div>
    </>
  );
}

// ───── Small render helpers ─────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.4px',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        margin: '0 0 8px 0',
        paddingBottom: 6,
        borderBottom: '1px solid var(--border)',
        fontFamily: 'monospace',
      }}
    >
      {children}
    </div>
  );
}

function PeriodCol({ kicker, value }: { kicker: string; value: string }) {
  return (
    <div style={{ flex: 1, minWidth: 110 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          letterSpacing: '1.4px',
          fontFamily: 'monospace',
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function DateCol({ kicker, value }: { kicker: string; value: string }) {
  return (
    <div style={{ flex: 1, minWidth: 90 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          letterSpacing: '1.2px',
          fontFamily: 'monospace',
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function StatBox({
  label,
  value,
  bg,
}: {
  label: string;
  value: number;
  bg?: string;
}) {
  return (
    <div
      style={{
        background: bg ?? '#F8FAFC',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: 'var(--text-primary)',
          letterSpacing: '-0.4px',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 9,
          fontFamily: 'monospace',
          fontWeight: 700,
          letterSpacing: '1.2px',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}
